import {Component, Element, Event, EventEmitter, h, Host, Prop, Watch} from '@stencil/core';
import {generatedInputNameAndId} from '@/utils';
import {ChangeDetail} from '../JoySelectableItemGroup/selectable-item.interfaces';

/**
 * @slot default - The default slot for a single line label
 * @slot selectable-item-sublabel - Slot used to render a sublabel (won't be bold if item is selected)
 */
@Component({
    tag: 'joy-selectable-item',
    styleUrl: 'selectable-item.scss',
    scoped: true,
})
export class SelectableItem {
    private multipleChoice = true;
    private fullWidth = false;

    @Element() host!: HTMLJoySelectableItemElement;
    /** Name property for form participation **/
    @Prop({reflect: true}) name: string | undefined;
    /** Disabled state **/
    @Prop({reflect: true}) disabled = false;
    /** Checked state **/
    @Prop({mutable: true, reflect: true}) checked = false;
    /** If you use this component with a joy-checkbox as slot: set the value only on the joy-checkbox, it will be automatically applied to it's parent **/
    @Prop({reflect: true}) value!: string;
    /** Required state **/
    @Prop({reflect: true}) required = false;

    @Event({eventName: 'value-change'}) valueChange!: EventEmitter<ChangeDetail>;
    @Event({eventName: 'joy-selectable-item-change'}) joySelectableItemChange!: EventEmitter<Partial<ChangeDetail>>;

    @Watch('checked')
    updateCheckbox(newValue: boolean) {
        if (!this.multipleChoice) {
            return;
        }

        this.host.querySelector('joy-checkbox')!.checked = newValue;
    }

    get computedId() {
        return this.host.id ? this.host.id + '-input' : null;
    }

    private onCheckboxChange = (ev: CustomEvent) => {
        this.checked = ev.detail;
        this.emitEventsOnModelUpdate();
    };

    private emitEventsOnModelUpdate() {
        const model = {
            value: this.value,
            checked: this.checked,
        };

        this.joySelectableItemChange.emit(model);
    }

    private onRadioChange = () => {
        if (!this.disabled) {
            this.checked = true;
            this.emitEventsOnModelUpdate();
        }
    };

    get ariaDisabled() {
        return this.disabled ? {['aria-disabled']: 'true'} : null;
    }

    private onClick = () => {
        if (!this.multipleChoice && !this.disabled) {
            // We've bound the onClick on the label in order to stop propagation and prevent multiple events firing
            this.checked = false;
            this.emitEventsOnModelUpdate();
        }
    };

    private onLabelClick = (e: any) => {
        // Why ? Because our label is the area we actually click on.
        // We don't want to catch label click, only parent/host to more easily test scenarios
        e.stopPropagation();
    };

    connectedCallback() {
        const group = this.host.parentElement as HTMLJoySelectableItemGroupElement;
        if (group.tagName === 'JOY-SELECTABLE-ITEM-GROUP') {
            this.multipleChoice = group.multiple;
            this.fullWidth = group.fullWidth;
        }
    }

    render() {
        const LabelTag = this.multipleChoice ? 'div' : 'label';
        let props = {};
        if (!this.multipleChoice) {
            Object.assign(props, {
                htmlFor: this.computedId || generatedInputNameAndId(this.host),
                onClick: this.onLabelClick,
            });
        }

        return (
            <Host data-choice={this.multipleChoice ? 'multiple' : 'single'} full-width={this.fullWidth} onClick={this.onClick} {...this.ariaDisabled}>
                <div class="joy-selectable-item__wrapper">
                    {!this.multipleChoice && (
                        <input
                            id={this.computedId || generatedInputNameAndId(this.host)}
                            checked={this.checked}
                            type="radio"
                            disabled={this.disabled}
                            onChange={this.onRadioChange}
                            value={this.value}
                            name={this.name}
                            required={this.required}
                        />
                    )}
                    <LabelTag
                        {...props}
                        class={{
                            'joy-selectable-item': true,
                            'joy-selectable-item--disabled': this.disabled,
                            'joy-selectable-item--checked': this.checked,
                            'joy-selectable-item--multiple': this.multipleChoice,
                            'joy-selectable-item--single': !this.multipleChoice,
                        }}
                    >
                        {this.multipleChoice && (
                            <joy-checkbox
                                id={this.computedId || generatedInputNameAndId(this.host)}
                                checked={this.checked}
                                disabled={this.disabled}
                                value={this.value}
                                name={this.name}
                                onJoy-checkbox-change={this.onCheckboxChange}
                                required={this.required}
                            >
                                <slot />
                            </joy-checkbox>
                        )}
                        {!this.multipleChoice && <slot />}
                        <slot name="selectable-item-sublabel" />
                    </LabelTag>
                </div>
            </Host>
        );
    }
}
